// Queue access — who may open /queue: a business with its own queue subscription, OR an active employee
// (sub-account) of a business, who works that business's queue ("Operating as"). The server enforces which
// business queues an operator may use (requireQueueOperator); this only decides whether to offer the page.

import { useEffect, useState } from 'react';
import { useWallet } from '../contexts/WalletContext.tsx';
import { useSubscription } from '../contexts/SubscriptionContext.tsx';
import { listBusinesses } from '../api/subAccounts.ts';

export type QueueAccess = Readonly<{
  readonly known: boolean;     // enough is loaded to decide
  readonly allowed: boolean;
  readonly businesses: ReadonlyArray<string>;   // active businesses this user works for (handles)
}>;

export const canOperateQueue = (hasOwnQueue: boolean, activeBusinessCount: number): boolean =>
  hasOwnQueue || activeBusinessCount > 0;

export const useQueueAccess = (): QueueAccess => {
  const { connectedWallet } = useWallet();
  const { hasFeature, state: subState } = useSubscription();
  const sessionId = connectedWallet?.sessionId ?? null;
  const [businesses, setBusinesses] = useState<ReadonlyArray<string> | null>(null);

  useEffect(() => {
    setBusinesses(null);
    if (sessionId === null) return;
    let cancelled = false;
    void listBusinesses(sessionId).then((result) => {
      if (cancelled) return;
      setBusinesses(result.ok
        ? Object.freeze(result.value.filter((b) => b.status === 'active').map((b) => b.parent_handle))
        : Object.freeze([]));
    });
    return () => { cancelled = true; };
  }, [sessionId]);

  const ownQueue = hasFeature('queue');
  const subscriptionKnown = !subState.loading && (subState.status !== null || subState.error !== null);
  const list = businesses ?? [];
  return Object.freeze({
    known: ownQueue || (subscriptionKnown && businesses !== null),
    allowed: canOperateQueue(ownQueue, list.length),
    businesses: list,
  });
};
